import Grid from "@mui/material/Grid";
import {
  Autocomplete,
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import React, { useEffect, useState } from "react";

import AssignmentSummaryCard from "./AssignmentSummaryCard";
import { PublishedOrDraftToggle } from "../../../shared-components/PublishedOrDraftToggle";
import { MyClassesOrAllClassesToggle } from "../../../shared-components/MyClassesOrAllClassesToggle";
import { useApiClient } from "../../../api";
import { useAuthenticationStore } from "../../../stores/auth";
import { usePagination } from "../../../hooks/usePagination";
import GeneralLoading from "../../../shared-components/Loading";
import TieredFeatureAccess from "../../../shared-components/TieredFeatureAccess";
import { AcademicYearSelect } from "../../../shared-components/AcademicYearSelect";

const SEARCH_DEBOUNCE_MS = 200;

export const FruitifyAssignmentSearch = ({
  forSchoolClass = null,
  excludeTitle = false,
  showAnswerCount = false,
  studentView = false,
}) => {
  const statusOptions = ["Upcoming", "Past Deadline"];

  const ApiClient = useApiClient();
  const { user } = useAuthenticationStore();

  const [searchTerm, setSearchTerm] = useState("");

  const [debouncedValue, setDebouncedValue] = useState("");

  const [selectedClass, setSelectedClass] = useState(forSchoolClass || null);

  const [classSearchTerm, setClassSearchTerm] = useState("");

  const [debouncedClassSearchTerm, setDebouncedClassSearchTerm] = useState("");

  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);

  const [published, setPublished] = useState(true);

  const [showAllClasses, setShowAllClasses] = useState(false);

  const [selectedAcademicYear, setSelectedAcademicYear] = useState(
    forSchoolClass?.academicYear?.id,
  );

  /*
   * Debounce assignment search.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  /*
   * Debounce class search.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedClassSearchTerm(classSearchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(handler);
    };
  }, [classSearchTerm]);

  /*
   * Fetch available classes.
   *
   * Searching is server-side, so the
   * Autocomplete itself must not apply
   * another client-side text filter.
   */
  const {
    options: schoolClasses,
    loading: schoolClassesLoading,
    pageInfo: schoolClassesPageInfo,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.schools.getSchoolClasses({
        userRole: user.info.currentRole,
        usersOnly: !showAllClasses,
        showAllClasses,
        searchTerm: debouncedClassSearchTerm,
        academicYear: selectedAcademicYear || null,
        first,
        after,
      }),
    20,
    {
      userRole: user.info.currentRole,
      showAllClasses,
      debouncedClassSearchTerm,
      selectedAcademicYear,
    },
    !forSchoolClass,
  );

  /*
   * A class supplied directly by the
   * parent is always selected and does
   * not need to be fetched.
   */
  useEffect(() => {
    if (forSchoolClass) {
      setSelectedClass(forSchoolClass);
      setSelectedAcademicYear(forSchoolClass.academicYear?.id || null);
    }
  }, [forSchoolClass]);

  /*
   * Auto-select if the UNFILTERED query
   * proves that the user genuinely only
   * has one available class.
   *
   * Do not do this for search results:
   * a search returning one class does
   * not mean the user only has one class.
   */
  useEffect(() => {
    if (
      forSchoolClass ||
      debouncedClassSearchTerm ||
      schoolClassesLoading ||
      !schoolClassesPageInfo
    ) {
      return;
    }

    if (schoolClasses.length === 1 && !schoolClassesPageInfo.hasNextPage) {
      setSelectedClass(schoolClasses[0]);
    }
  }, [
    forSchoolClass,
    debouncedClassSearchTerm,
    schoolClassesLoading,
    schoolClasses,
    schoolClassesPageInfo,
  ]);

  /*
   * Changing between My Classes and
   * All Classes changes the available
   * class scope, so reset the selected
   * class and search.
   */
  useEffect(() => {
    if (forSchoolClass) {
      return;
    }

    setSelectedClass(null);
    setClassSearchTerm("");
    setDebouncedClassSearchTerm("");
  }, [showAllClasses, forSchoolClass]);

  const {
    options: assignments,
    page,
    pageInfo,
    handlePageChange,
    loading,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.assignment.getAllAssignments({
        schoolClass: selectedClass?.id || null,
        isPublished: published,
        showAllClasses,
        searchString: debouncedValue,
        selectedStatus,
        academicYear: selectedAcademicYear || null,
        first,
        after,
      }),
    12,
    {
      selectedClass,
      published,
      showAllClasses,
      debouncedValue,
      selectedStatus,
      selectedAcademicYear,
    },
  );

  const classOptions = forSchoolClass ? [forSchoolClass] : schoolClasses;

  return (
    <Grid
      container
      spacing={2}
      sx={{
        width: "100%",
        mt: 6,
      }}
      data-testid="fruitify-assignment-search"
    >
      {!excludeTitle && (
        <Grid size={12} sx={{ mb: 2 }}>
          <Typography variant="h2">Assignments</Typography>
        </Grid>
      )}

      <Grid size={12} sx={{ mb: 2 }}>
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              height: 50,
            },
          }}
        >
          <TextField
            label="Search Keyword"
            variant="outlined"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 240,
              },
            }}
          />

          <AcademicYearSelect
            value={selectedAcademicYear}
            onChange={(academicYear) => {
              setSelectedAcademicYear(academicYear);
              if (
                selectedClass?.academicYear?.id &&
                selectedClass.academicYear.id !== academicYear
              ) {
                setSelectedClass(null);
              }
            }}
            disabled={Boolean(forSchoolClass)}
            sx={{ width: { xs: "100%", sm: 200 } }}
          />

          <Autocomplete
            options={classOptions}
            value={selectedClass}
            loading={schoolClassesLoading}
            disabled={Boolean(forSchoolClass)}
            disableClearable={Boolean(forSchoolClass)}
            /*
             * The backend is already
             * filtering these results.
             */
            filterOptions={(options) => options}
            onInputChange={(_event, value, reason) => {
              /*
               * Only actual typing should
               * become a backend search.
               *
               * MUI also fires this with
               * reason="reset" after an
               * option is selected. We
               * intentionally ignore that
               * so selecting "Maths 10A"
               * doesn't immediately turn
               * that label into a search.
               */
              if (reason === "input") {
                setClassSearchTerm(value);

                /*
                 * If the user starts typing
                 * after selecting a class,
                 * they are looking for a new
                 * class, so remove the old
                 * assignment filter.
                 */
                if (selectedClass && value !== selectedClass.name) {
                  setSelectedClass(null);
                }
              }

              if (reason === "clear") {
                setClassSearchTerm("");
              }
            }}
            onChange={(_event, schoolClass) => {
              /*
               * This is the actual class
               * selection. MUI manages the
               * displayed input label from
               * `value`, so we do not need
               * to control `inputValue`.
               */
              setSelectedClass(schoolClass);

              /*
               * Once selected, return the
               * options query to its normal
               * unfiltered state.
               */
              setClassSearchTerm("");
              setDebouncedClassSearchTerm("");
            }}
            getOptionLabel={(schoolClass) => schoolClass?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={
              schoolClassesLoading ? "Loading classes..." : "No classes found"
            }
            renderInput={(params) => (
              <TextField {...params} label="Class" placeholder="All classes" />
            )}
            sx={{
              width: {
                xs: "100%",
                sm: 240,
              },
            }}
          />

          {published && (
            <FormControl
              sx={{
                width: {
                  xs: "100%",
                  sm: 240,
                },
              }}
            >
              <InputLabel id="status-label">Status</InputLabel>

              <Select
                labelId="status-label"
                value={selectedStatus}
                label="Status"
                onChange={(event) => setSelectedStatus(event.target.value)}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!studentView && !forSchoolClass && (
            <MyClassesOrAllClassesToggle
              showAllClasses={showAllClasses}
              setShowAllClasses={setShowAllClasses}
            />
          )}

          {!studentView && (
            <PublishedOrDraftToggle
              published={published}
              setPublished={setPublished}
            />
          )}
        </Stack>
      </Grid>

      {loading ? (
        <GeneralLoading />
      ) : (
        assignments.map((assignment) => (
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
            key={assignment.id}
            sx={{
              display: "flex",
            }}
          >
            <TieredFeatureAccess
              accessOverride={assignment.canAccessOnTier}
              tooltipMessage="Upgrade to unlock historic assignments"
            >
              <AssignmentSummaryCard
                assignment={assignment}
                showAnswerCount={showAnswerCount}
                studentView={studentView}
              />
            </TieredFeatureAccess>
          </Grid>
        ))
      )}

      {page && pageInfo && handlePageChange && (
        <Grid size={12}>
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              page={page}
              onChange={handlePageChange}
              count={pageInfo.hasNextPage ? page + 1 : page}
              color="primary"
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
